import { useTranslation } from 'react-i18next';
import TopBar from '../home/components/TopBar';
import MainNav from '../home/components/MainNav';
import Footer from '../home/components/Footer';
import QuickMenu from '../../components/feature/QuickMenu';
import { useSEO, createServiceSchema } from '../../utils/seo';

export default function EnterpriseSolutionPage() {
  const { t } = useTranslation();

  useSEO({
    title: '기업 솔루션 - KR STUDIO | 기업교육 콘텐츠 제작',
    description: 'KR스튜디오의 기업 맞춤형 솔루션으로 효과적인 임직원 교육을 실현하세요. 기업 문화와 목표에 맞는 최적의 교육 콘텐츠를 제공합니다.',
    keywords: '기업 솔루션, 기업교육, 임직원 교육, 사내교육, 기업 콘텐츠',
    canonical: '/enterprise-solution',
    schema: createServiceSchema(
      '기업 솔루션',
      '기업 문화와 목표에 맞는 효과적인 임직원 교육 콘텐츠 제작'
    ),
  });

  const solutions = [
    {
      icon: 'ri-building-line',
      title: '대기업 맞춤 솔루션',
      description: '글로벌 기업을 위한 엔터프라이즈급 콘텐츠 플랫폼',
      features: ['전용 스튜디오', '전담 팀 배정', 'SLA 보장', '24/7 지원']
    },
    {
      icon: 'ri-shield-check-line',
      title: '보안 & 컴플라이언스',
      description: '기업 보안 정책 및 규정 준수',
      features: ['데이터 암호화', '접근 권한 관리', 'ISO 인증', '감사 로그']
    },
    {
      icon: 'ri-team-line',
      title: '대규모 협업',
      description: '수백 명의 사용자가 동시에 작업',
      features: ['멀티 워크스페이스', '역할 기반 권한', '승인 워크플로우', '버전 관리']
    },
    {
      icon: 'ri-line-chart-line',
      title: '고급 분석',
      description: '상세한 사용 현황 및 ROI 분석',
      features: ['실시간 대시보드', '커스텀 리포트', 'API 연동', '데이터 내보내기']
    }
  ];

  const clients = [
    {
      name: '삼성전자',
      industry: '전자/IT',
      useCase: '글로벌 제품 교육 콘텐츠',
      result: '20개국 동시 배포, 교육 시간 60% 단축'
    },
    {
      name: '현대자동차',
      industry: '자동차',
      useCase: '딜러 교육 및 고객 안내',
      result: '전국 딜러 통합 교육, 일관된 품질 유지'
    },
    {
      name: 'SK텔레콤',
      industry: '통신',
      useCase: '사내 교육 및 기술 세미나',
      result: '월 100개 이상 콘텐츠 제작, 비용 40% 절감'
    },
    {
      name: 'LG화학',
      industry: '화학',
      useCase: '안전 교육 및 기술 문서',
      result: '다국어 안전 교육 표준화, 사고율 감소'
    }
  ];

  const features = [
    { title: '무제한 사용자', description: '추가 비용 없이 전 직원 이용', icon: 'ri-user-add-line' },
    { title: '전용 인프라', description: '독립적인 서버 및 스토리지', icon: 'ri-server-line' },
    { title: 'SSO 연동', description: '기존 인증 시스템과 통합', icon: 'ri-key-2-line' },
    { title: 'API 제공', description: '기존 시스템과 완벽 연동', icon: 'ri-code-s-slash-line' },
    { title: '맞춤 개발', description: '기업 요구사항 반영', icon: 'ri-tools-line' },
    { title: '전담 지원', description: '전담 매니저 및 기술 지원', icon: 'ri-customer-service-line' }
  ];

  const packages = [
    {
      name: 'Professional',
      price: '문의',
      users: '100명',
      features: [
        '월 50개 콘텐츠 제작',
        '100GB 스토리지',
        '기본 분석 리포트',
        '이메일 지원',
        'LMS 연동',
        '기본 템플릿'
      ]
    },
    {
      name: 'Enterprise',
      price: '문의',
      users: '500명',
      features: [
        '월 200개 콘텐츠 제작',
        '500GB 스토리지',
        '고급 분석 대시보드',
        '전화/이메일 지원',
        'SSO 연동',
        '맞춤 템플릿',
        'API 액세스',
        '전담 매니저'
      ],
      popular: true
    },
    {
      name: 'Ultimate',
      price: '문의',
      users: '무제한',
      features: [
        '무제한 콘텐츠 제작',
        '무제한 스토리지',
        '실시간 분석 & AI 인사이트',
        '24/7 전담 지원',
        '전용 인프라',
        '맞춤 개발',
        '전용 스튜디오',
        'SLA 99.9% 보장'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopBar />
      <MainNav />

      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/17b6fa43063b32fc1f73eb11bcbd20ad.jpg"
            alt="기업 솔루션"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/95 via-violet-900/90 to-purple-900/85"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-6xl font-black text-white mb-6 leading-tight">
              기업 솔루션
            </h1>
            <p className="text-2xl text-white/90 mb-8 leading-relaxed">
              대기업을 위한<br />
              엔터프라이즈급 콘텐츠 플랫폼
            </p>
            <div className="flex gap-4">
              <a href="mailto:contact@krstudio.com" className="bg-indigo-500 text-white px-10 py-4 text-lg font-bold hover:bg-indigo-600 transition cursor-pointer whitespace-nowrap hidden">
                도입 문의
              </a>
              <a href="#clients" className="border-2 border-white text-white px-10 py-4 text-lg font-bold hover:bg-white hover:text-indigo-900 transition cursor-pointer whitespace-nowrap hidden">
                고객 사례
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">엔터프라이즈 기능</h2>
            <p className="text-xl text-gray-600">대기업을 위한 특별한 기능</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {solutions.map((solution, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 hover:shadow-xl transition-all group cursor-pointer border border-gray-100">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-all">
                  <i className={`${solution.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">{solution.title}</h3>
                <p className="text-gray-600 mb-6">{solution.description}</p>
                <ul className="space-y-2">
                  {solution.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-700">
                      <i className="ri-check-line text-indigo-500 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">주요 고객사</h2>
            <p className="text-xl text-gray-600">대한민국 대표 기업들이 선택한 솔루션</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {clients.map((client, index) => (
              <div key={index} className="bg-white p-8 hover:shadow-xl transition-all cursor-pointer">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-black mb-2">{client.name}</h3>
                    <span className="inline-block bg-indigo-100 text-indigo-700 px-3 py-1 text-sm font-bold">
                      {client.industry}
                    </span>
                  </div>
                  <i className="ri-building-4-line text-4xl text-indigo-500"></i>
                </div>
                <div className="mb-4">
                  <h4 className="font-bold text-gray-700 mb-2">활용 사례</h4>
                  <p className="text-gray-600">{client.useCase}</p>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-bold text-gray-700 mb-2">도입 효과</h4>
                  <p className="text-indigo-600 font-bold">{client.result}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">핵심 기능</h2>
            <p className="text-xl text-gray-600">기업이 필요로 하는 모든 것</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-8 hover:shadow-lg transition-all cursor-pointer">
                <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">요금제</h2>
            <p className="text-xl text-gray-600">기업 규모에 맞는 플랜 선택</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg, index) => (
              <div key={index} className={`bg-white p-8 hover:shadow-xl transition-all cursor-pointer ${pkg.popular ? 'border-4 border-indigo-500 relative' : 'border border-gray-200'}`}>
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-indigo-500 text-white px-6 py-1 font-bold text-sm whitespace-nowrap">
                    가장 인기있는
                  </div>
                )}
                <h3 className="text-2xl font-black mb-2">{pkg.name}</h3>
                <div className="text-4xl font-black text-indigo-500 mb-4">{pkg.price}</div>
                <div className="text-gray-600 mb-6">최대 {pkg.users}</div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-indigo-500 mr-2 mt-1"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a href="mailto:contact@krstudio.com" className={`block text-center py-3 font-bold transition whitespace-nowrap ${pkg.popular ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'border-2 border-indigo-500 text-indigo-500 hover:bg-indigo-500 hover:text-white'}`}>
                  문의하기
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">보안 & 컴플라이언스</h2>
            <p className="text-xl text-gray-600">기업 데이터를 안전하게 보호</p>
          </div>
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-8">
              <i className="ri-shield-check-line text-5xl text-indigo-500 mb-4"></i>
              <h3 className="text-2xl font-bold mb-4">보안 인증</h3>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <i className="ri-check-line text-indigo-500 mr-2"></i>
                  <span>ISO 27001 인증</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-indigo-500 mr-2"></i>
                  <span>SOC 2 Type II 준수</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-indigo-500 mr-2"></i>
                  <span>GDPR 완전 준수</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-indigo-500 mr-2"></i>
                  <span>개인정보보호법 준수</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 p-8">
              <i className="ri-lock-line text-5xl text-indigo-500 mb-4"></i>
              <h3 className="text-2xl font-bold mb-4">데이터 보호</h3>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <i className="ri-check-line text-indigo-500 mr-2"></i>
                  <span>AES-256 암호화</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-indigo-500 mr-2"></i>
                  <span>전송 중 TLS 1.3</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-indigo-500 mr-2"></i>
                  <span>정기 보안 감사</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-indigo-500 mr-2"></i>
                  <span>백업 및 재해 복구</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-black mb-6">기업 솔루션을 도입하세요</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            대한민국 대표 기업들이 선택한 콘텐츠 플랫폼
          </p>
          <a href="mailto:contact@krstudio.com" className="inline-block bg-indigo-500 text-white px-12 py-4 text-lg font-bold hover:bg-indigo-600 transition cursor-pointer whitespace-nowrap hidden">
            무료 컨설팅 신청
          </a>
        </div>
      </section>

      <Footer />
      <QuickMenu />
    </div>
  );
}
